import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box,
  IconButton,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Grid
} from '@mui/material';
import { 
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  GetApp as GetAppIcon,
  Check as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { ExcelRenderer } from 'react-excel-renderer';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import MasterJobService from '../../services/masterJobService';

const MasterJobImport = ({ open, onClose, onSuccess }) => {
  // Estados
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedData, setUploadedData] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importStats, setImportStats] = useState(null);
  
  // Pasos del wizard
  const steps = ['Subir archivo Excel', 'Revisar datos', 'Importar'];
  
  // Manejar carga de archivo
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Verificar tipo de archivo
    const fileType = file.name.split('.').pop().toLowerCase();
    if (fileType !== 'xlsx' && fileType !== 'xls' && fileType !== 'csv') {
      setError('Por favor, suba un archivo Excel (.xlsx, .xls) o CSV (.csv)');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    ExcelRenderer(file, (err, resp) => {
      setLoading(false);
      
      if (err) {
        console.error('Error al procesar archivo Excel:', err);
        setError('Error al procesar el archivo. Verifique el formato e intente de nuevo.');
        return;
      }
      
      try {
        // Extraer encabezados y filas
        const headers = resp.rows[0];
        const rows = resp.rows.slice(1).filter(row => row.length > 0 && row[0]);
        
        // Validar columnas requeridas
        const requiredColumns = [
          'Kode Pekerjaan', 
          'Nama Pekerjaan', 
          'Kategori/Sub Divisi', 
          'Satuan (BoQ)'
        ];
        
        // Mapear índices de columnas
        const columnMap = {};
        headers.forEach((header, index) => {
          requiredColumns.forEach(reqCol => {
            if (header.includes(reqCol)) {
              columnMap[reqCol] = index;
            }
          });
        });
        
        // Verificar columnas faltantes
        const missingColumns = requiredColumns.filter(col => columnMap[col] === undefined);
        
        if (missingColumns.length > 0) {
          setError(`Columnas faltantes: ${missingColumns.join(', ')}. Por favor, descargue la plantilla y siga el formato.`);
          return;
        }
        
        // Transformar datos al formato del modelo
        const transformedData = rows.map(row => {
          return {
            kode_pekerjaan: row[columnMap['Kode Pekerjaan']] || '',
            nama_pekerjaan: row[columnMap['Nama Pekerjaan']] || '',
            kategori: row[columnMap['Kategori/Sub Divisi']] || '',
            volume_default: parseFloat(row[headers.indexOf('Volume Default')] || 0),
            satuan: row[columnMap['Satuan (BoQ)']] || '',
            tipe_input: row[headers.indexOf('Tipe Input')] || 'manual'
          };
        });
        
        // Validar datos
        const invalidRows = transformedData.filter(row => {
          return !row.kode_pekerjaan || 
                 !row.nama_pekerjaan || 
                 !row.kategori || 
                 !row.satuan ||
                 !/^\d+\.\d+\.\d+\.\d+$/.test(row.kode_pekerjaan);
        });
        
        if (invalidRows.length > 0) {
          setError(`Se encontraron ${invalidRows.length} filas inválidas. Verifique que todos los campos requeridos estén completos y que los códigos de trabajo tengan el formato correcto (X.X.X.XX).`);
          return;
        }
        
        setUploadedData(file);
        setParsedRows(transformedData);
        setActiveStep(1);
      } catch (error) {
        console.error('Error procesando datos de Excel:', error);
        setError('Error al procesar los datos. Verifique el formato e intente de nuevo.');
      }
    });
  };
  
  // Manejar descarga de plantilla
  const handleDownloadTemplate = () => {
    const template = MasterJobService.generateExcelTemplate();
    
    // Crear worksheet
    const worksheet = XLSX.utils.json_to_sheet(template.sampleData, {
      header: template.headers
    });
    
    // Configurar anchos de columna
    const wscols = [
      { wch: 15 },  // Kode Pekerjaan
      { wch: 35 },  // Nama Pekerjaan
      { wch: 20 },  // Kategori/Sub Divisi
      { wch: 15 },  // Volume Default
      { wch: 15 },  // Satuan (BoQ)
      { wch: 15 }   // Tipe Input
    ];
    worksheet['!cols'] = wscols;
    
    // Crear workbook y añadir worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Master Jobs');
    
    // Generar archivo Excel
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Guardar archivo
    saveAs(blob, 'master_jobs_template.xlsx');
  };
  
  // Manejar importación
  const handleImport = async () => {
    if (!uploadedData || parsedRows.length === 0) {
      setError('No hay datos para importar');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Preparar FormData para enviar el archivo
      const formData = new FormData();
      formData.append('file', uploadedData);
      
      const response = await MasterJobService.importMasterJobs(formData);
      
      if (response.success) {
        setImportStats(response.stats);
        setActiveStep(2);
      } else {
        setError('Error al importar datos: ' + response.message);
      }
    } catch (err) {
      console.error('Error importando datos:', err);
      setError('Error al importar datos: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };
  
  // Manejar cierre
  const handleClose = () => {
    // Si se completó la importación, notificar éxito
    if (activeStep === 2 && !error) {
      onSuccess();
    } else {
      onClose();
    }
    
    // Reiniciar estado
    setActiveStep(0);
    setUploadedData(null);
    setParsedRows([]);
    setLoading(false);
    setError(null);
    setImportStats(null);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: '#FFF8E1', color: '#5D4037', fontWeight: 'bold', p: 2 }}>
        Importar Pekerjaan Master dari Excel
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#5D4037'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}
        
        {activeStep === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Suba un archivo Excel con los datos de los trabajos maestros. El archivo debe incluir columnas para Kode Pekerjaan, Nama Pekerjaan, Kategori/Sub Divisi, y Satuan (BoQ).
              Puede descargar una plantilla para empezar.
            </Alert>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                border: '1px dashed #5D4037',
                borderRadius: 1,
                bgcolor: '#FAFAFA',
                textAlign: 'center'
              }}
            >
              <Box sx={{ mb: 2 }}>
                <CloudUploadIcon sx={{ fontSize: 60, color: '#F0C14B', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'medium', color: '#5D4037' }}>
                  Arrastre su archivo aquí o haga clic para seleccionar
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Formatos soportados: .xlsx, .xls, .csv
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                component="label"
                sx={{ 
                  bgcolor: '#F0C14B', 
                  color: '#5D4037',
                  '&:hover': { bgcolor: '#DDA726' },
                  mx: 1
                }}
              >
                Seleccionar Archivo
                <input
                  type="file"
                  hidden
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileUpload}
                />
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                sx={{ 
                  color: '#5D4037', 
                  borderColor: '#5D4037',
                  '&:hover': { bgcolor: '#F9F5EB' },
                  mx: 1
                }}
                startIcon={<GetAppIcon />}
              >
                Descargar Plantilla
              </Button>
            </Paper>
            
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress sx={{ color: '#F0C14B' }} />
              </Box>
            )}
          </Box>
        )}
        
        {activeStep === 1 && parsedRows.length > 0 && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckIcon sx={{ mr: 1 }} />
                <Typography>
                  Se analizaron correctamente {parsedRows.length} trabajos maestros del archivo Excel. Revise los datos y presione "Importar" para continuar.
                </Typography>
              </Box>
            </Alert>
            
            <TableContainer component={Paper} elevation={0} sx={{ mb: 3, maxHeight: '300px' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FFF8E1' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Kode Pekerjaan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Nama Pekerjaan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Kategori</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Volume Default</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Satuan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Tipe Input</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedRows.slice(0, 10).map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{row.kode_pekerjaan}</TableCell>
                      <TableCell>{row.nama_pekerjaan}</TableCell>
                      <TableCell>{row.kategori}</TableCell>
                      <TableCell>{row.volume_default}</TableCell>
                      <TableCell>{row.satuan}</TableCell>
                      <TableCell>{row.tipe_input}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {parsedRows.length > 10 && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Mostrando 10 de {parsedRows.length} registros.
              </Typography>
            )}
            
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress sx={{ color: '#F0C14B' }} />
              </Box>
            )}
          </Box>
        )}
        
        {activeStep === 2 && importStats && (
          <Box>
            <Alert 
              severity={importStats.error > 0 ? "warning" : "success"} 
              sx={{ mb: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {importStats.error > 0 ? <ErrorIcon sx={{ mr: 1 }} /> : <CheckIcon sx={{ mr: 1 }} />}
                <Typography>
                  Importación completada. {importStats.success} de {importStats.total} registros importados correctamente.
                  {importStats.error > 0 ? ` Se encontraron ${importStats.error} errores.` : ''}
                </Typography>
              </Box>
            </Alert>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#F9F9F9' }}>
                  <Typography variant="h4" sx={{ color: '#5D4037', fontWeight: 'bold' }}>
                    {importStats.total}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total de registros
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={4}>
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#F0F8FF' }}>
                  <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    {importStats.success}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Importados con éxito
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={4}>
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#FFF8F8' }}>
                  <Typography variant="h4" sx={{ color: '#F44336', fontWeight: 'bold' }}>
                    {importStats.error}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Errores
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, bgcolor: '#FAFAFA' }}>
        <Button 
          onClick={handleClose}
          sx={{ color: '#5D4037' }}
        >
          {activeStep === 2 ? 'Cerrar' : 'Cancelar'}
        </Button>
        
        {activeStep === 1 && (
          <Button 
            variant="contained"
            onClick={handleImport}
            disabled={loading}
            sx={{ 
              bgcolor: '#F0C14B', 
              color: '#5D4037',
              '&:hover': { bgcolor: '#DDA726' }
            }}
          >
            Importar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MasterJobImport; 